import type { ReactNode } from "react";
import { canvasPanelClassNames, cx } from "../canvasTheme";
import type { SettingsPage } from "../settingsPanelTypes";

type TabButtonProps = {
  page: SettingsPage;
  activePage: SettingsPage;
  children: ReactNode;
  onClick: (page: SettingsPage) => void;
};

export function TabButton({ page, activePage, children, onClick }: TabButtonProps) {
  return (
    <button
      type="button"
      className={cx(
        canvasPanelClassNames.tabButton,
        activePage === page ? canvasPanelClassNames.tabActive : canvasPanelClassNames.tabInactive,
      )}
      onClick={() => onClick(page)}
    >
      {children}
    </button>
  );
}

type BaseFieldProps = {
  label: string;
  className?: string;
  children: ReactNode;
};

export function Field({ label, className, children }: BaseFieldProps) {
  return (
    <label className={cx(canvasPanelClassNames.fieldLabel, className)}>
      {label}
      {children}
    </label>
  );
}

type TextInputFieldProps = {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  onCommit?: () => void;
  className?: string;
  disabled?: boolean;
  inputMode?: "decimal" | "numeric";
  max?: number;
  min?: number;
  name?: string;
  step?: number;
  type?: "text" | "number";
};

export function TextInputField({
  label,
  value,
  onChange,
  onCommit,
  className,
  disabled,
  inputMode,
  max,
  min,
  name,
  step,
  type = "text",
}: TextInputFieldProps) {
  return (
    <Field label={label} className={className}>
      <input
        className={disabled ? canvasPanelClassNames.disabledControl : canvasPanelClassNames.control}
        disabled={disabled}
        inputMode={inputMode}
        max={max}
        min={min}
        name={name}
        step={step}
        type={type}
        value={value}
        onBlur={onCommit}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") onCommit?.();
        }}
      />
    </Field>
  );
}

type SelectFieldProps<T extends string> = {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  className?: string;
};

export function SelectField<T extends string>({ label, value, options, onChange, className }: SelectFieldProps<T>) {
  return (
    <Field label={label} className={className}>
      <select
        className={canvasPanelClassNames.selectControl}
        value={value}
        onChange={(event) => onChange(event.target.value as T)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

type RangeFieldProps = {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
};

export function RangeField({ label, value, min, max, step, onChange }: RangeFieldProps) {
  return (
    <Field label={label}>
      <input
        className={canvasPanelClassNames.control}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </Field>
  );
}
